import { Button } from '@components/common';
import { Pencil, Trash2 } from 'lucide-react';
import React from 'react';
import { InventorySupplierRecord } from '@/renderer/types/inventory';

function SupplierStatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        isActive
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

export function SuppliersTable({
  suppliers,
  onEdit,
  onDelete,
}: {
  suppliers: InventorySupplierRecord[];
  onEdit: (supplier: InventorySupplierRecord) => void;
  onDelete: (supplier: InventorySupplierRecord) => void;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="max-h-[34rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Supplier
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Contact
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Terms
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Opening balance
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((supplier) => (
              <tr key={supplier.id} className="border-t border-gray-100">
                <td className="px-5 py-4">
                  <div className="font-medium text-gray-900">
                    {supplier.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {supplier.tax_id || 'No tax ID'}
                  </div>
                </td>
                <td className="px-5 py-4 text-gray-600">
                  <div>{supplier.contact_person || 'No contact person'}</div>
                  <div className="text-xs text-gray-500">
                    {supplier.phone || supplier.email || 'No phone or email'}
                  </div>
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {supplier.payment_terms_days} day
                  {supplier.payment_terms_days === 1 ? '' : 's'}
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {supplier.opening_balance.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td className="px-5 py-4">
                  <SupplierStatusBadge isActive={supplier.is_active} />
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => onEdit(supplier)}
                    >
                      <Pencil size={14} />
                      Edit
                    </Button>
                    <Button
                      variant="text"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(supplier)}
                    >
                      <Trash2 size={14} />
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
